import { MediaDownloadDto, MediaUploadDto, WikiMediaOverlayElementDownloadDto, WikiMediaOverlayElementsDownloadDto } from 'collaboration-service';
import { MediaType } from 'components/MediaSelection/FileSelector';
import MediaSelection from 'components/MediaSelection/MediaSelection';
import Editor, { editorEmptyString } from "Dummies/TestImpl/TestEditor";
import { AutoGrid, AutoGridColumn, Button, Icon, styled } from 'imaginarity-react-ui';
import _ from 'lodash';
import * as React from 'react';
import { useAppSelector } from 'services/ApplicationState/ApplicationState';
import ScrollHelper from 'services/Helpers/ScrollHelper';
import { useImgI18N } from 'services/ImgI18N';
import WikiHotSpot from 'views/Wiki/WikiHotSpot';
import { WikiSC as T } from './WikiSC';

export interface ExtendedOverlayElementData extends WikiMediaOverlayElementDownloadDto {
    file?: File;
}

export interface ExtendedOverlayData extends WikiMediaOverlayElementsDownloadDto {
    elements: ExtendedOverlayElementData[];
}

export interface WikiEditHotspotProps {
    t: (key: string, options?: any) => string;
    overlayData: ExtendedOverlayData;
    imgWidth: number;
    imgHeight: number;
    offsetX?: number;
    offsetY?: number;
    overlayDataChanged: (overlayData: ExtendedOverlayData) => void;
    width: number;
    children?: React.ReactNode;
    showHelp?: boolean;
}

const D = styled.div`
    display: inline-block;
    color: ${props => props.theme.colors.accent};
`;

const WikiEditHotspot: React.FC<WikiEditHotspotProps> = ({
    t,
    overlayData,
    imgWidth,
    imgHeight,
    offsetX = 0,
    offsetY = 0,
    overlayDataChanged,
    width,
    children,
    showHelp
}) => {
    const [editMode, setEditMode] = React.useState(-1);
    const scrollHelper = React.useMemo(() => new ScrollHelper(), []);
    const containerRef = React.useRef<HTMLDivElement>(null);
    const [overlayDataLocal, setOverlayDataLocal] = React.useState<ExtendedOverlayData>(overlayData);
    const editorNeedSave = !_.isEqual(overlayData, overlayDataLocal);

    const marginTop = offsetY + 10;
    const aspectRatio = isNaN(imgWidth / imgHeight) ? (16 / 9) : (imgWidth / imgHeight);
    const imgPadding = 100 / aspectRatio;

    const addOverlayDataElement = React.useCallback((x: number, y: number) => {
        const newOverlayData = _.cloneDeep(overlayData);
        const elem: WikiMediaOverlayElementDownloadDto = {
            content: "",
            positionX: Math.round(x),
            positionY: Math.round(y),
            type: "HotSpotTooltip",
            media: {} as MediaDownloadDto,
        };
        if (!newOverlayData.elements) {
            newOverlayData.elements = [];
        }
        newOverlayData.elements.push(elem);
        overlayDataChanged(newOverlayData);
    }, [overlayData, overlayDataChanged]);

    const onContextMenu = React.useCallback((event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        event.preventDefault();
        const t = event.target as HTMLDivElement;
        let found = false;
        let p: HTMLElement | null = t;
        while (p && p !== null) {
            if (p.className.indexOf("hspc") >= 0) {
                found = true;
                break;
            }
            p = p.parentElement;
        }
        if (t && found) {
            const r = t.getBoundingClientRect();
            const x = event.clientX - r.left;
            const y = event.clientY - r.top;
            const px = x * 100 / r.width;
            const py = y * 100 / r.height;
            addOverlayDataElement(px, py);
        }
        return false;
    }, [addOverlayDataElement]);

    const deleteOverlayElement = React.useCallback((i: number) => () => {
        const newOverlayData = _.clone(overlayData);
        newOverlayData.elements.splice(i, 1);
        overlayDataChanged(newOverlayData);
        setEditMode(-1);
    }, [overlayData, overlayDataChanged]);

    const onMoved = React.useCallback((i: number) => (x: number, y: number) => {
        if (containerRef.current) {
            const r = containerRef.current.getBoundingClientRect();
            const dpx = x / r.width * 100;
            const dpy = y / r.height * 100;
            const newOverlayData = _.clone(overlayData);
            const e = newOverlayData.elements[i];
            e.positionX = Math.max(0.0, Math.min(100.0, e.positionX + dpx));
            e.positionY = Math.max(0.0, Math.min(100.0, e.positionY + dpy));
            overlayDataChanged(newOverlayData);
        }
    }, [overlayData, overlayDataChanged]);

    const shift = React.useCallback((i: number, direction: string, largeStep?: boolean) => () => {
        const newOverlayData = _.clone(overlayData);
        const step = largeStep ? 5 : 0.2;
        const element = newOverlayData.elements[i];

        switch (direction) {
            case "left":
                if (element.positionX > 0) element.positionX -= step;
                break;
            case "right":
                if (element.positionX < 100) element.positionX += step;
                break;
            case "up":
                if (element.positionY > 0) element.positionY -= step;
                break;
            case "down":
                if (element.positionY < 100) element.positionY += step;
                break;
        }
        overlayDataChanged(newOverlayData);
    }, [overlayData, overlayDataChanged]);

    const mediaChanged = React.useCallback((i: number) => (file: File, media: MediaUploadDto) => {
        const newOverlayData = _.clone(overlayData);
        newOverlayData.elements[i].media = media as MediaDownloadDto;
        newOverlayData.elements[i].file = file;
        overlayDataChanged(newOverlayData);
    }, [overlayData, overlayDataChanged]);

    const mediaRemoved = React.useCallback((i: number) => () => {
        const newOverlayData = _.clone(overlayData);
        newOverlayData.elements[i].media = { ...newOverlayData.elements[i].media, changed: true, links: [] } as MediaDownloadDto;
        newOverlayData.elements[i].file = undefined;
        overlayDataChanged(newOverlayData);
    }, [overlayData, overlayDataChanged]);

    // const onKeyPress = React.useCallback((i: number) => (event: React.KeyboardEvent<HTMLInputElement>) => {
    //     const newOverlayData = _.clone(overlayData);
    //     const ctrl = event.ctrlKey;
    //     const alt = event.altKey;
    //     let moved = false;

    //     if (ctrl || alt) {
    //         const step = alt ? 5 : 0.2;
    //         const element = newOverlayData.elements[i];

    //         switch (event.keyCode) {
    //             case 37: // left
    //                 if (element.positionX > 0) {
    //                     moved = true;
    //                     element.positionX -= step;
    //                 }
    //                 break;
    //             case 39: // right
    //                 if (element.positionX < 100) {
    //                     moved = true;
    //                     element.positionX += step;
    //                 }
    //                 break;
    //             case 38: // up
    //                 if (element.positionY > 0) {
    //                     moved = true;
    //                     element.positionY -= step;
    //                 }
    //                 break;
    //             case 40: // down
    //                 if (element.positionY < 100) {
    //                     moved = true;
    //                     element.positionY += step;
    //                 }
    //                 break;
    //         }
    //     }

    //     if (moved) {
    //         event.preventDefault();
    //         event.stopPropagation();
    //         overlayDataChanged(newOverlayData);
    //     }
    // }, [overlayData, overlayDataChanged]);

    const edit = React.useCallback((i: number) => (show: boolean) => {
        if (show) {
            setEditMode(i);
            scrollHelper.scrollTo("editor-area");
        } else {
            setEditMode(-1);
            scrollHelper.scrollTo("top");
        }
    }, [scrollHelper]);
    const saveEditorContents = React.useMemo(() => () => {
        overlayDataChanged(overlayDataLocal);
    }, [overlayDataLocal, overlayDataChanged]);

    const editorOnChange = React.useCallback((html: string) => {
        if (editMode >= 0) {
            const newOverlayData = _.cloneDeep(overlayData);
            const overlayElement = newOverlayData.elements[editMode];
            if (overlayElement === undefined) return;

            overlayElement.content = html === editorEmptyString ? "" : html;
            newOverlayData.elements[editMode] = overlayElement;
            setOverlayDataLocal(newOverlayData);
        }
    }, [editMode, overlayData]);

    const oneColumnLayout = width <= 768;
    const noInfo = width <= 400;

    return (
        <T.HotSpotContainerOuter>
            <T.HotSpotContainer
                paddingTop={imgPadding}
                onContextMenu={onContextMenu}
                ref={containerRef}
            >
                <T.HotSpotContainerImage className="hspc" ref={scrollHelper.getRef("top")}>
                    <T.HotSpotContainerImageNoPointer>
                        {children}
                    </T.HotSpotContainerImageNoPointer>
                </T.HotSpotContainerImage>

                {_.map(overlayData.elements, (e, i) => (
                    <WikiHotSpot
                        key={i}
                        index={i + 1}
                        element={e}
                        delay={0}
                        imgWidth={imgWidth ?? 400}
                        imgHeight={imgHeight ?? 280}
                        offsetX={offsetX}
                        offsetY={offsetY}
                        width={width}
                        selected={editMode === i}
                        editMode
                        editHotspot={edit(i)}
                        onMoved={onMoved(i)}
                    />
                ))}
            </T.HotSpotContainer>

            {(overlayData.elements || showHelp) && (
                <T.OverlayElement className="OverlayElement" marginTop={marginTop} ref={scrollHelper.getRef("editor-area")}>
                    <AutoGrid columns={oneColumnLayout ? 1 : 5}>
                        <AutoGridColumn span={4}>
                            <T.EditorStyled padded={!oneColumnLayout}>
                                <T.EditorStyledTitle>
                                    <h3>{t("edit hotspot")}</h3>
                                </T.EditorStyledTitle>

                                {editMode >= 0 && (
                                    <>
                                        <T.ButtonGrid>
                                            <T.ShiftUpWide>
                                                <Button
                                                    kind="transparentButton"
                                                    icon="chevron up double"
                                                    onClick={shift(editMode, "up", true)}
                                                />
                                            </T.ShiftUpWide>
                                            <T.ShiftRightWide>
                                                <Button
                                                    kind="transparentButton"
                                                    icon="chevron right double"
                                                    onClick={shift(editMode, "right", true)}
                                                />
                                            </T.ShiftRightWide>
                                            <T.ShiftDownWide>
                                                <Button
                                                    kind="transparentButton"
                                                    icon="chevron down double"
                                                    onClick={shift(editMode, "down", true)}
                                                />
                                            </T.ShiftDownWide>
                                            <T.ShiftLeftWide>
                                                <Button
                                                    kind="transparentButton"
                                                    icon="chevron left double"
                                                    onClick={shift(editMode, "left", true)}
                                                />
                                            </T.ShiftLeftWide>
                                            <T.InfoLeft>&#177; 5.0%</T.InfoLeft>
                                            {!noInfo && (
                                                <T.InfoShift>
                                                    {t("move hotspot")}
                                                </T.InfoShift>
                                            )}
                                            <T.InfoRight>&#177; 0.2%</T.InfoRight>
                                            <T.ShiftUp>
                                                <Button
                                                    kind="transparentButton"
                                                    icon="chevron up"
                                                    onClick={shift(editMode, "up")}
                                                />
                                            </T.ShiftUp>
                                            <T.ShiftRight>
                                                <Button
                                                    kind="transparentButton"
                                                    icon="chevron right"
                                                    onClick={shift(editMode, "right")}
                                                />
                                            </T.ShiftRight>
                                            <T.ShiftDown>
                                                <Button
                                                    kind="transparentButton"
                                                    icon="chevron down"
                                                    onClick={shift(editMode, "down")}
                                                />
                                            </T.ShiftDown>
                                            <T.ShiftLeft>
                                                <Button
                                                    kind="transparentButton"
                                                    icon="chevron left"
                                                    onClick={shift(editMode, "left")}
                                                />
                                            </T.ShiftLeft>
                                            <T.ButtonGridButtonsBtm>
                                                <D>
                                                    <Icon name="hashtag" style={{ float: "left" }} color={"@accent"} />
                                                    {editMode + 1}
                                                </D>
                                            </T.ButtonGridButtonsBtm>
                                            <T.ButtonGridButtonsTop>
                                                <Icon
                                                    name="hotspot"
                                                    size="30px"
                                                    marginTop={5}
                                                    color={"@middleLightGrey"}
                                                />
                                            </T.ButtonGridButtonsTop>
                                        </T.ButtonGrid>

                                        <MediaSelection
                                            media={overlayData.elements[editMode].media}
                                            uploadType={MediaType.IMAGE || MediaType.SVG}
                                            onMediaSelected={mediaChanged(editMode)}
                                            imgAspect={16 / 9}
                                            onDelete={mediaRemoved(editMode)}
                                        />
                                        <div style={{ position: "relative", }}>
                                            <Editor
                                                value={overlayData.elements[editMode]?.content ?? ""}
                                                onChange={editorOnChange}
                                            />
                                            {editorNeedSave &&
                                                <div style={{ position: "absolute", top: -40, right: 0 }}>
                                                    <Button kind='primary' icon='save' onClick={saveEditorContents} />
                                                </div>
                                            }
                                        </div>
                                    </>
                                )}
                                <div style={{ margin: "10px 0" }}>
                                    <T.EditorStyledInfo>
                                        <Icon name="info" style={{ float: "left", marginRight: 5, marginTop: 3 }} />
                                        {t("right click to add new hotspot")}
                                    </T.EditorStyledInfo>
                                    <T.EditorStyledInfo>
                                        <Icon name="info" style={{ float: "left", marginRight: 5, marginTop: 3 }} />
                                        {t("select hotspot by clicking the edit icon")}
                                    </T.EditorStyledInfo>
                                    <T.EditorStyledInfo>
                                        <Icon name="info" style={{ float: "left", marginRight: 5 }} />
                                        {t("move hotspot by drag and drop or use the arrow buttons")}
                                    </T.EditorStyledInfo>
                                    <T.EditorStyledInfo>
                                        <Icon name="info" style={{ float: "left", marginRight: 5, marginTop: 3 }} />
                                        {t("edit description by clicking in the text box")}
                                    </T.EditorStyledInfo>
                                </div>

                            </T.EditorStyled>
                        </AutoGridColumn>

                        <AutoGridColumn span={1}>
                            <div style={{ paddingLeft: !oneColumnLayout ? 20 : 0 }}>
                                {_.map(overlayData.elements, (e, i) => {
                                    const index = i + 1;
                                    const editSelected = editMode === i;

                                    return (
                                        <T.InputElement edit={editSelected} smallDisplay={oneColumnLayout} key={i}>
                                            <Button
                                                kind={editSelected ? "primary" : "transparentButton"}
                                                icon={editSelected ? "edit close" : "edit"}
                                                onClick={() => editSelected ? setEditMode(-1) : setEditMode(i)}
                                                floated="left"
                                            />
                                            {editSelected && (
                                                <Button
                                                    kind="halfTransparentButton"
                                                    icon="delete"
                                                    floated="right"
                                                    onClick={deleteOverlayElement(editMode)}
                                                />
                                            )}
                                            <T.Info>
                                                <Icon name="hashtag" style={{ float: "left" }} />
                                                {index}
                                            </T.Info>
                                        </T.InputElement>
                                    );
                                })}
                            </div>
                        </AutoGridColumn>
                    </AutoGrid>
                </T.OverlayElement>
            )}
        </T.HotSpotContainerOuter>
    );
};

const TranslatedWikiEditHotspot: React.FC<Omit<WikiEditHotspotProps, 't'>> = (props) => {
    const config = useAppSelector(state => state.wikiState.config);
    const { t } = useImgI18N(config.namespace ?? "wiki");

    return <WikiEditHotspot t={t} {...props} />;
};

export default TranslatedWikiEditHotspot;
